import { FIELDS_TYPES, isEmptyValue } from '../utils/constants/fields';
import { PUNCTUATION } from '../utils/constants/notation';
import { OPTION_KEYS } from '../utils/constants/optionKeys';
import { normalizeContactEnrichmentStatus } from '../utils/enrichmentStatus';
import { applyRegisterStatus } from '../utils/companyStatus';
import { bucketLabelForSurfeSeniority } from '../utils/constants/roleBuckets';
import {
  calculateAge,
  cleanWebsiteUrl,
  cleanWebsiteUrl_SAFE,
  cleanWebsiteUrl_SAFE_LOGO,
  extractFirstUrl,
  formatNumber,
  formatPercent
} from '../utils/utils';

const genericMapper = (entry, fields, toRequest = false) => {
  const fieldsConfig = Object.values(fields).reduce(
    (acc, curr) => Object.assign(acc, { [curr.NAME]: curr }),
    {}
  );

  if (fieldsConfig) {
    Object.keys(fieldsConfig).forEach((fieldName) => {
      if (entry[fieldName] === undefined) {
        entry[fieldName] = null;
      }
    });
  }

  Object.entries(entry).forEach(([fieldName, fieldValue]) => {
    const fieldConfig = fieldsConfig[fieldName];
    if (fieldConfig?.TYPE === FIELDS_TYPES.TYPE_NUMBER) {
      let number = fieldValue;
      if (fieldConfig.INTEGER) {
        if (number !== '' && number !== null && number !== undefined) {
          number = toRequest ? parseInt(number) : '' + number;
        }
      } else if (number !== '' && number !== null && number !== undefined) {
        number = toRequest ? parseFloat(number) : number.toFixed(2);
      }

      if (toRequest) {
        if (typeof number !== 'number') {
          entry[fieldName] = null;
        } else {
          entry[fieldName] = number;
        }
      } else {
        if (number === null || number === undefined) {
          entry[fieldName] = '';
        } else {
          entry[fieldName] = number;
        }
      }
    } else if (
      fieldConfig &&
      [
        FIELDS_TYPES.TYPE_EMAIL,
        FIELDS_TYPES.TYPE_PASSWORD,
        FIELDS_TYPES.TYPE_TEXT,
        FIELDS_TYPES.TYPE_TEXTAREA
      ].includes(fieldConfig.TYPE)
    ) {
      if (!entry[fieldName]) {
        entry[fieldName] = '';
      }
    } else if (
      FIELDS_TYPES.TYPE_CHECKBOX === fieldConfig?.TYPE &&
      typeof entry[fieldName] !== 'boolean'
    ) {
      entry[fieldName] = false;
    } else if (
      fieldConfig &&
      (fieldConfig.TYPE === FIELDS_TYPES.TYPE_SELECT ||
        fieldConfig.TYPE === FIELDS_TYPES.TYPE_RADIO)
    ) {
      if (isEmptyValue(entry[fieldName])) {
        entry[fieldName] = toRequest ? null : [];
      } else {
        if (toRequest) {
          if (!isNaN(entry[fieldName]?.[0])) {
            entry[fieldName] = +entry[fieldName][0];
          } else if (typeof entry[fieldName]?.[0] === 'string') {
            entry[fieldName] = entry[fieldName][0];
          }
        } else {
          if (!isNaN(entry[fieldName])) {
            entry[fieldName] = [+entry[fieldName]];
          } else if (typeof entry[fieldName] === 'string') {
            entry[fieldName] = [entry[fieldName]];
          }
        }
      }
    } else if (
      FIELDS_TYPES.TYPE_MULTI_SELECT === fieldConfig?.TYPE &&
      !Array.isArray(entry[fieldName])
    ) {
      entry[fieldName] = [];
    }
  });
  return entry;
};

export const mapData = (data, fields = {}, toRequest = false) => {
  // const newData = toRequest ? JSON.parse(JSON.stringify(data)) : JSON.parse(JSON.stringify(data));
  const newData = JSON.parse(JSON.stringify(data));
  if (Array.isArray(newData)) {
    return newData.map((entry) => genericMapper(entry, fields, toRequest));
  } else {
    return genericMapper(newData, fields, toRequest);
  }
};

const mapPhoneCodesOptions = (codes = []) => {
  const data = [];
  codes.forEach(({ name, value, country }) =>
    value.split(',').forEach((value) => data.push({ name: `${name} ${value}`, value, country }))
  );
  return data;
};

export const mapOptions = (options = {}) => {
  options[OPTION_KEYS.PHONE_CODES] = mapPhoneCodesOptions(options[OPTION_KEYS.PHONE_CODES]);

  const industriesRawList = options[OPTION_KEYS.PROFILER_INDUSTRY_CODES] || [];
  delete options[OPTION_KEYS.PROFILER_INDUSTRY_CODES];

  const flattenTree = (nodes, parent = null) => {
    const flatList = [];

    for (const node of nodes) {
      const newNode = {
        ...node,
        parent: parent
          ? {
              value: parent.value,
              name: parent.name
            }
          : null
      };

      const { children, ...nodeWithoutChildren } = newNode;
      flatList.push(nodeWithoutChildren);

      if (node.children && node.children.length > 0) {
        flatList.push(...flattenTree(node.children, node));
      }
    }

    return flatList;
  };

  const addParentReferences = (nodes, parent = null) => {
    return nodes.map((node) => {
      const newNode = { ...node };
      if (parent) {
        newNode.parent = { value: parent.value, name: parent.name };
      }
      if (newNode.children) {
        newNode.children = addParentReferences(newNode.children, newNode);
      }
      return newNode;
    });
  };

  const treeWithParents = addParentReferences(industriesRawList);
  const flatList = flattenTree(industriesRawList);

  options[OPTION_KEYS.PROFILER_INDUSTRY_CODES] = {
    rawList: treeWithParents,
    flatList: flatList,
    getInitialListsCopy: (selections = []) => {
      const flatListCopy = JSON.parse(JSON.stringify(flatList));

      // `selections` carries the minimal covering set (see collapseIndustryCodes),
      // so a stored 'C' has to re-select its whole subtree. Ancestry is string
      // prefixing, which also means a list saved in the old fully expanded format
      // still resolves to exactly the same nodes.
      const isSelected = (value) =>
        selections.some((selection) => value === selection || value.startsWith(selection));

      flatListCopy.forEach((item) => {
        item.selected = isSelected(item.value);
        item.toggled = false;
      });

      const valueToNodeMap = new Map();
      flatListCopy.forEach((item) => valueToNodeMap.set(item.value, item));

      flatListCopy.forEach((item) => {
        item.children = [];

        if (item.parent) {
          const parentNode = valueToNodeMap.get(item.parent.value);
          if (parentNode) {
            parentNode.children = parentNode.children || [];
            parentNode.children.push(item);
          }
        }
      });

      const result = {
        rawList: JSON.parse(JSON.stringify(options[OPTION_KEYS.PROFILER_INDUSTRY_CODES].rawList)),
        flatList: flatListCopy
      };

      // console.log(
      //   'options[OPTION_KEYS.PROFILER_INDUSTRY_CODES].getInitialListsCopy(selections)',
      //   result
      // );

      return result;
    }
  };

  return options;
};

export const getMergeFieldConfig = (fields = []) => {
  return Object.fromEntries(fields.map((f, i) => [i, f]));
};

const isProfilerScoringEnabled = import.meta.env.VITE_PROFILER_SCORING_ENABLED === 'true';
const mapCompanyScoring = !isProfilerScoringEnabled
  ? () => {}
  : (company) => {
      if (typeof company.score !== 'number') {
        company.showScoring = false;
        return;
      }

      company.showScoring = true;
      // if (company.score >= 0.85) {
      //   company.scoreLabel = 'Very high';
      //   company.scoreTooltipText = company.rationale;
      //   company.scorePrimaryColor = '#166C3B';
      //   company.scoreSecondaryColor = '#EAF6ED';
      //   company.scoreTooltipClassName = 'scoring-tooltip very-high';
      //   company.scoreIconFilter =
      //     'invert(29%) sepia(6%) saturate(7179%) hue-rotate(99deg) brightness(99%) contrast(83%)';
      // } else if (company.score >= 0.7) {
      //   company.scoreLabel = 'High';
      //   company.scoreTooltipText = company.rationale;
      //   company.scorePrimaryColor = '#845201';
      //   company.scoreSecondaryColor = '#FDF2DC';
      //   company.scoreTooltipClassName = 'scoring-tooltip high';
      //   company.scoreIconFilter =
      //     'invert(34%) sepia(99%) saturate(5864%) hue-rotate(46deg) brightness(94%) contrast(99%)';
      // } else if (company.score >= 0.5) {
      //   company.scoreLabel = 'Medium';
      //   company.scoreTooltipText = company.rationale;
      //   company.scorePrimaryColor = '#5E5E5E';
      //   company.scoreSecondaryColor = '#F3F3F3';
      //   company.scoreTooltipClassName = 'scoring-tooltip medium';
      //   company.scoreIconFilter =
      //     'invert(37%) sepia(0%) saturate(0%) hue-rotate(40deg) brightness(94%) contrast(86%)';
      // } else {
      //   company.scoreLabel = 'Low';
      //   company.scoreTooltipText = company.rationale;
      //   company.scorePrimaryColor = '#DC3545';
      //   company.scoreSecondaryColor = '#FAD9D9';
      //   company.scoreTooltipClassName = 'scoring-tooltip low';
      //   company.scoreIconFilter =
      //     'invert(26%) sepia(94%) saturate(984%) hue-rotate(324deg) brightness(112%) contrast(96%)';
      // }

      if (company.score >= 0.8) {
        company.scoreLabel = 'High';
        company.scoreTooltipText = company.rationale;
        company.scorePrimaryColor = '#166C3B';
        company.scoreSecondaryColor = '#EAF6ED';
        company.scoreTooltipClassName = 'scoring-tooltip very-high';
        company.scoreIconFilter =
          'invert(29%) sepia(6%) saturate(7179%) hue-rotate(99deg) brightness(99%) contrast(83%)';
      } else if (company.score >= 0.4) {
        company.scoreLabel = 'Medium';
        company.scoreTooltipText = company.rationale;
        company.scorePrimaryColor = '#5E5E5E';
        company.scoreSecondaryColor = '#F3F3F3';
        company.scoreTooltipClassName = 'scoring-tooltip medium';
        company.scoreIconFilter =
          'invert(37%) sepia(0%) saturate(0%) hue-rotate(40deg) brightness(94%) contrast(86%)';
      } else {
        company.showScoring = false;
      }
    };

const formatCompanyFieldValueToEM = (value) => {
  if (value && !isNaN(value)) {
    return formatNumber((Number(value) / 1000000).toFixed(1), PUNCTUATION.COMMA, true);
  }
};

const _formatPercent = (nonFormattedPercentage) => {
  if (nonFormattedPercentage && !isNaN(nonFormattedPercentage)) {
    return formatPercent(
      (Number(nonFormattedPercentage) * 100).toFixed(1),
      PUNCTUATION.COMMA,
      true
    );
  }

  return nonFormattedPercentage;
};

export const mapCompanyEntry = (company) => {
  const primaryWebsite = extractFirstUrl(company.website);
  [company.website, company.website_SAFE] = [
    cleanWebsiteUrl(primaryWebsite),
    cleanWebsiteUrl_SAFE(primaryWebsite)
  ];
  company.website_SAFE_LOGO = cleanWebsiteUrl_SAFE_LOGO(company.website_SAFE);
  company.geographies = company.geoFocus || [];
  company.industries = company.industryKeywords || [];
  company.valueChain = company.valueChainKeywords || [];
  company.products = company.productPortfolioKeywords || [];
  company._revenue = company.revenue?.value;
  [
    company.revenue,
    company.revenueYear,
    company.revenuePercentage,
    company._revenuePercentage,
    company._revenueSource
  ] = [
    formatCompanyFieldValueToEM(company?.revenue?.value),
    company?.revenue?.year,
    _formatPercent(company?.revenue?.percentage),
    company?.revenue?.percentage,
    company?.revenue?.source
  ];
  if (
    company.revenue &&
    company.revenue !== PUNCTUATION.EMPTY_VALUE &&
    company._revenueSource === 'basic'
  ) {
    company.isRevenueBasic = true;
  }
  company._grossProfit = company.grossProfit?.value;
  [
    company.grossProfit,
    company.grossProfitYear,
    company.grossProfitPercentage,
    company._grossProfitPercentage
  ] = [
    formatCompanyFieldValueToEM(company?.grossProfit?.value),
    company?.grossProfit?.year,
    _formatPercent(company?.grossProfit?.percentage),
    company?.grossProfit?.percentage
  ];
  if (
    (!company.revenue || company.revenue === PUNCTUATION.EMPTY_VALUE) &&
    company.grossProfit &&
    company.grossProfit !== PUNCTUATION.EMPTY_VALUE
  ) {
    company._revenue = company._grossProfit;
    company.revenue = company.grossProfit;
    company.revenueYear = company.grossProfitYear;
    company.revenuePercentage = company.grossProfitPercentage;
    company._revenuePercentage = company._grossProfitPercentage;
    company.isRevenueGrossProfit = true;
  }
  company._ebit = company?.ebit?.value;
  company._ebitPercentage = company?.ebit?.percentage;
  [company.ebit, company.ebitYear, company.ebitPercentage] = [
    formatCompanyFieldValueToEM(company?.ebit?.value),
    company?.ebit?.year,
    _formatPercent(company?.ebit?.percentage)
  ];

  company._cagr3Y = company?.cagr3Y?.value;
  [
    company.cagr3Y,
    company.cagr3YStart,
    company.cagr3YEnd,
    company.cagr3YBasedOn,
    company.cagr3YScope
  ] = [
    _formatPercent(company?.cagr3Y?.value),
    company.cagr3Y?.startYear,
    company.cagr3Y?.endYear,
    company.cagr3Y?.basedOn,
    company.cagr3Y?.scope
  ];

  company._cagr4Y = company?.cagr4Y?.value;
  [
    company.cagr4Y,
    company.cagr4YStart,
    company.cagr4YEnd,
    company.cagr4YBasedOn,
    company.cagr4YScope
  ] = [
    _formatPercent(company?.cagr4Y?.value),
    company.cagr4Y?.startYear,
    company.cagr4Y?.endYear,
    company.cagr4Y?.basedOn,
    company.cagr4Y?.scope
  ];

  company.productPortfolio = company.productPortfolioDescription;

  if (Array.isArray(company.employees) && company.employees.length) {
    company.employeesArray = company.employees
      .sort((a, b) => b.year - a.year)
      .map((entry) => ({
        ...entry,
        value: +entry.value,
        shortYear: entry.year?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE
      }));

    const latestYearEmployeesEntries = company.employeesArray
      .filter((entry) => entry.year === company.employeesArray[0].year)
      .sort((a, b) => (b.value = a.value));

    company.employeesYear = latestYearEmployeesEntries[0]?.year;
    company.isEmployeesBasic =
      !!latestYearEmployeesEntries[0]?.value && latestYearEmployeesEntries[0]?.source === 'basic';
    company.employees = latestYearEmployeesEntries[0]?.value;
    company._employees = company.employees;

    // const onlyActualLatestYearEmployeesEntries = latestYearEmployeesEntries.filter(
    //   (entry) => entry.reliability_code === 9092
    // );

    // if (onlyActualLatestYearEmployeesEntries.length) {
    //   company.employeesYear = onlyActualLatestYearEmployeesEntries[0]?.year;
    //   company.employees = onlyActualLatestYearEmployeesEntries[0]?.value;
    // } else {
    //   company.employeesYear = latestYearEmployeesEntries[0]?.year;
    //   company.employees = latestYearEmployeesEntries[0]?.value;
    // }
  } else {
    company._employees = company?.employees?.value;
    company.employeesArray = null;
    company.employeesYear = company?.employees?.year;
    company.isEmployeesBasic = !!company._employees && company?.employees?.source === 'basic';
    company.employees = company?.employees?.value;
  }

  if (Array.isArray(company.businessVerticals)) {
    const businessVerticals = [];
    company.businessVerticals.forEach((totalBSData) => {
      const properties = Object.entries(totalBSData);
      const description = properties.find(([key]) => key === 'Description')?.[1];
      const name = properties.find(([key]) => key !== 'Description')?.[1]; // Business Vertical 1, Business Vertical 2 etc. are bad properties for mapping
      businessVerticals.push({ name, description });
    });
    company.businessVerticals = businessVerticals;
  } else {
    company.businessVerticals = [];
  }

  if (Array.isArray(company.keyProducts)) {
    const keyProducts = [];
    company.keyProducts.forEach((totalBSData) => {
      const properties = Object.entries(totalBSData);
      const description = properties.find(([key]) => key === 'Description')?.[1];
      const name = properties.find(([key]) => key !== 'Description')?.[1]; // Product 1, Product 2 etc. are bad properties for mapping
      keyProducts.push({ name, description });
    });
    company.keyProducts = keyProducts;
  } else {
    company.keyProducts = [];
  }

  // This is bad randomized mock [ START ]
  // const getRandomLetter = () => {
  //   const letters = ['A', 'B', 'T'];
  //   const randomIndex = Math.floor(Math.random() * letters.length);
  //   return letters[randomIndex];
  // };
  // company._industryCode = getRandomLetter();
  // const getRandomNumber = (min, max) => {
  //   return Math.floor(Math.random() * (max - min + 1)) + min;
  // };
  // company._heatmapEbit = getRandomNumber(-100, 100);
  // company._heatmapEbitMargin = getRandomNumber(-10, 10);
  // company._heatmapCagr3Y = getRandomNumber(-10, 10);
  // company._heatmapCagr4Y = getRandomNumber(-10, 10);
  // const revenue = parseFloat(company._revenue);
  // company._heatmapRevenue = isNaN(revenue) ? 0 : revenue;
  // This is bad randomized mock [  END  ]

  company._industryCode = (company.industryCode || '').slice(0, 1) || 'unknown';
  const ebit = parseFloat(company._ebit);
  company._heatmapEbit = isNaN(ebit) ? 0 : ebit;
  const ebitMargin = parseFloat(company.ebitPercentage);
  company._heatmapEbitMargin = isNaN(ebitMargin) ? 0 : ebitMargin;
  const cagr3Y = parseFloat(company.cagr3Y);
  company._heatmapCagr3Y = isNaN(cagr3Y) ? 0 : cagr3Y;
  const cagr4Y = parseFloat(company.cagr4Y);
  company._heatmapCagr4Y = isNaN(cagr4Y) ? 0 : cagr4Y;
  const revenue = parseFloat(company._revenue);
  company._heatmapRevenue = isNaN(revenue) ? 0 : revenue;
  const revenuePercentage = parseFloat(company._revenuePercentage);
  company._heatmapRevenuePercentage = isNaN(revenuePercentage)
    ? 0
    : parseFloat(_formatPercent(revenuePercentage));

  mapCompanyScoring(company);

  // Per-basis ownership figures from the backend; null on older backends.
  company.ownershipSummary = company.ownershipSummary ?? null;

  // Register status (TD-2948): an array for the Add filter panel, ranks for the sort menu,
  // and whether the list read itself carried a status (the gate for both).
  company.companyStatus = company.companyStatus ?? null;
  company.companyStatusSince = company.companyStatusSince ?? null;
  company.companyStatusSincePrecision = company.companyStatusSincePrecision ?? null;
  applyRegisterStatus(company);
  company._registerStatusFromList = company._registerStatusRank != null;

  return company;
};

/**
 * Card figures of one shareholder graph node, from the flat graph-view fields. Follows the
 * search-list rules of mapCompanyEntry: revenue in EUR millions (the gross-profit value and year
 * when revenue is missing), the blue "basic" mark only for a basic revenue (never for the
 * gross-profit fallback), employees basic only when there is a headcount. Money values arrive as
 * decimal strings and are passed through unchanged. EBIT, CAGR and percentages are never mapped.
 */
const mapShareholderGraphNodeFigures = (node) => ({
  revenue: formatCompanyFieldValueToEM(node.revenue ?? node.gross_profit) ?? null,
  revenueYear: node.revenue_year ?? node.gross_profit_year ?? null,
  isRevenueBasic: node.revenue != null && node.revenue_source === 'basic',
  isRevenueGrossProfit: !!node.revenue_is_gross_profit,
  employees: node.employees ?? null,
  employeesYear: node.employees_year ?? null,
  isEmployeesBasic: !!node.employees && node.employees_source === 'basic',
  companyUuid: node.company_uuid ?? null
});

/**
 * NodeFigures of the shareholder graph view, keyed by String(member_id).
 * @param {Object[]} [nodes] - graph-view shareholder nodes
 * @returns {Map<string, Object>} empty for missing input; nodes without member_id are skipped
 */
export const mapShareholderGraphFigures = (nodes) => {
  const figuresByMemberId = new Map();
  if (!Array.isArray(nodes)) {
    return figuresByMemberId;
  }

  nodes.forEach((node) => {
    if (node?.member_id == null) {
      return;
    }
    figuresByMemberId.set(String(node.member_id), mapShareholderGraphNodeFigures(node));
  });

  return figuresByMemberId;
};

export const mapCompanySearchNameEntry = (company) => {
  const primaryWebsite = extractFirstUrl(company.website);
  [company.website, company.website_SAFE] = [
    cleanWebsiteUrl(primaryWebsite),
    cleanWebsiteUrl_SAFE(primaryWebsite)
  ];
  company.website_SAFE_LOGO = cleanWebsiteUrl_SAFE_LOGO(company.website_SAFE);
  company.industries = company.industry_keywords || [];
  company._revenue = company.revenue?.value;
  [
    company.revenue,
    company.revenueYear,
    company.revenuePercentage,
    company._revenuePercentage,
    company._revenueSource
  ] = [
    formatCompanyFieldValueToEM(company?.revenue?.value),
    company?.revenue?.year,
    _formatPercent(company?.revenue?.percentage),
    company?.revenue?.percentage,
    company?.revenue?.source
  ];
  if (
    company.revenue &&
    company.revenue !== PUNCTUATION.EMPTY_VALUE &&
    company._revenueSource === 'basic'
  ) {
    company.isRevenueBasic = true;
  }
  company._grossProfit = company.grossProfit?.value;
  [
    company.grossProfit,
    company.grossProfitYear,
    company.grossProfitPercentage,
    company._grossProfitPercentage
  ] = [
    formatCompanyFieldValueToEM(company?.grossProfit?.value),
    company?.grossProfit?.year,
    _formatPercent(company?.grossProfit?.percentage),
    company?.grossProfit?.percentage
  ];
  if (
    (!company.revenue || company.revenue === PUNCTUATION.EMPTY_VALUE) &&
    company.grossProfit &&
    company.grossProfit !== PUNCTUATION.EMPTY_VALUE
  ) {
    company._revenue = company._grossProfit;
    company.revenue = company.grossProfit;
    company.revenueYear = company.grossProfitYear;
    company.revenuePercentage = company.grossProfitPercentage;
    company._revenuePercentage = company._grossProfitPercentage;
    company.isRevenueGrossProfit = true;
  }

  if (Array.isArray(company.employees) && company.employees.length) {
    company.employeesArray = company.employees
      .sort((a, b) => b.year - a.year)
      .map((entry) => ({
        ...entry,
        value: +entry.value,
        shortYear: entry.year?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE
      }));

    const latestYearEmployeesEntries = company.employeesArray
      .filter((entry) => entry.year === company.employeesArray[0].year)
      .sort((a, b) => (b.value = a.value));

    company.employeesYear = latestYearEmployeesEntries[0]?.year;
    company.isEmployeesBasic =
      !!latestYearEmployeesEntries[0]?.value && latestYearEmployeesEntries[0]?.source === 'basic';
    company.employees = latestYearEmployeesEntries[0]?.value;
    company._employees = company.employees;

    // const onlyActualLatestYearEmployeesEntries = latestYearEmployeesEntries.filter(
    //   (entry) => entry.reliability_code === 9092
    // );

    // if (onlyActualLatestYearEmployeesEntries.length) {
    //   company.employeesYear = onlyActualLatestYearEmployeesEntries[0]?.year;
    //   company.employees = onlyActualLatestYearEmployeesEntries[0]?.value;
    // } else {
    //   company.employeesYear = latestYearEmployeesEntries[0]?.year;
    //   company.employees = latestYearEmployeesEntries[0]?.value;
    // }
  } else {
    company._employees = company?.employees?.value;
    company.employeesArray = null;
    company.employeesYear = company?.employees?.year;
    company.isEmployeesBasic = !!company._employees && company?.employees?.source === 'basic';
    company.employees = company?.employees?.value;
  }

  mapCompanyScoring(company);

  return company;
};

/**
 * Maps a bulk company-search poll payload onto the CompanyNameResultItem shape.
 *
 * SINGLE SHARED CONTRACT (anti-drift): every CompanyNameResultItem data source runs its raw
 * candidates through mapCompanySearchNameEntry. The bulk poll payload emits the same raw wire
 * shape as fetchCompaniesByName (revenue/employees as {value,year,source} objects,
 * industry_keywords as an array — NOT pre-formatted on the BE), so we just fan the existing
 * name-search mapper across rows[].candidates[]. Mutates candidates in place and returns results.
 */
export const mapBulkSearchResults = (results) => {
  (results?.rows || []).forEach((row) => (row.candidates || []).forEach(mapCompanySearchNameEntry));
  return results;
};

/**
 * Maps the add-bulk-company-candidate response (TD-2365): a single user-picked company for a
 * not-found row, returned in the same raw wire shape as a poll candidate. Runs the shared
 * name-search mapper over data.candidate exactly ONCE (mapCompanySearchNameEntry is NOT idempotent),
 * so the picked company renders identically to an auto-match. Mutates + returns data.
 */
export const mapBulkManualCandidate = (data) => {
  if (data?.candidate) {
    mapCompanySearchNameEntry(data.candidate);
  }
  return data;
};

/**
 * Maps individuals/persons search result entry from backend format to frontend format
 * Backend uses snake_case (first_name, last_name, birth_date)
 * Frontend expects camelCase (firstName, lastName, birthday)
 */
export const mapIndividualSearchEntry = (individual) => {
  if (!individual) return individual;

  // Map snake_case to camelCase
  if (individual.first_name !== undefined) {
    individual.firstName = individual.first_name;
  }
  if (individual.last_name !== undefined) {
    individual.lastName = individual.last_name;
  }
  if (individual.birth_date !== undefined) {
    individual.birthday = individual.birth_date;
  }
  if (individual.birth_year !== undefined) {
    individual.birthYear = individual.birth_year;
  }
  // TD-1709: personId (crefo_id) is used for fetching person details API
  // Backend returns personId alongside id (UUID) - personId is the lookup key
  if (individual.personId !== undefined) {
    individual.crefoId = individual.personId;
  }

  // These fields are already in the correct format: id, gender, location

  return individual;
};

/**
 * Maps individual/person details response from backend format to frontend format
 * Handles person data and their company relations
 */
export const mapIndividualDetailsEntry = (person) => {
  if (!person) return person;

  // Map snake_case to camelCase for person fields
  if (person.full_name !== undefined) {
    person.fullName = person.full_name;
  }
  if (person.crefo_id !== undefined) {
    person.crefoId = person.crefo_id;
  }
  if (person.house_number !== undefined) {
    person.houseNumber = person.house_number;
  }
  if (person.house_number_suffix !== undefined) {
    person.houseNumberSuffix = person.house_number_suffix;
  }

  // Build formatted address
  const addressParts = [
    person.street,
    [
      person.houseNumber || person.house_number,
      person.houseNumberSuffix || person.house_number_suffix
    ]
      .filter(Boolean)
      .join(''),
    person.postcode,
    person.city,
    person.country
  ].filter(Boolean);
  person.formattedAddress = addressParts.join(', ');

  // Map birth fields and calculate age
  // Backend returns birth_year (always) and birth_date (optional, variable granularity)
  if (person.birth_year !== undefined) {
    person.birthYear = person.birth_year;
  }
  if (person.birth_date !== undefined) {
    person.birthday = person.birth_date;
  }
  // Calculate age from birthday (full date) or birthYear (year only)
  person.age = calculateAge(
    person.birthday || person.birth_date,
    person.birthYear || person.birth_year
  );

  // Process company relations
  if (person.companies && Array.isArray(person.companies)) {
    person.companies.forEach((company) => {
      // TD-2079: normalise BE enrichment status ('success' → FE 'found') so the
      // Contact Detail section shows previously-revealed values inline.
      normalizeContactEnrichmentStatus(company);

      // Flatten roles for display (e.g., "Shareholder, Managing director")
      if (company.roles && Array.isArray(company.roles)) {
        company.roleDisplay = company.roles
          .map((r) => r.role)
          .filter(Boolean)
          .join(', ');

        // Get share percentage from shareholder role
        const shareholderRole = company.roles.find((r) =>
          r.role?.toLowerCase().includes('shareholder')
        );
        company.sharePercent = shareholderRole?.ownership_percent ?? null;

        // Get year from shareholder role for share display
        if (shareholderRole?.valid_from) {
          const validFromDate = new Date(shareholderRole.valid_from);
          company.shareYear = validFromDate.getFullYear();
        }

        // Get latest valid_from date for "Date of information" tooltip
        const latestRole = company.roles.reduce((latest, role) => {
          if (
            !latest ||
            (role.valid_from && new Date(role.valid_from) > new Date(latest.valid_from))
          ) {
            return role;
          }
          return latest;
        }, null);

        if (latestRole?.valid_from) {
          const infoDate = new Date(latestRole.valid_from);
          // Format as MM/YYYY
          company.dateOfInformation = `${String(infoDate.getMonth() + 1).padStart(2, '0')}/${infoDate.getFullYear()}`;
        }
      }

      // Map revenue fields (divide by 1M to show in €M like company search results)
      if (company.revenue) {
        company.revenueValue = formatCompanyFieldValueToEM(company.revenue.value);
        company.revenueYear = company.revenue.year;
        company.revenueSource = company.revenue.source;
        company.isRevenueBasic = company.revenue.source === 'basic';
      }

      // Map employees fields
      if (company.employees) {
        company.employeesValue = company.employees.value;
        company.employeesYear = company.employees.year;
        company.employeesSource = company.employees.source;
        company.isEmployeesBasic = company.employees.source === 'basic';
      }

      // Map is_ultimate (currently always null from backend)
      company.isUltimate = company.is_ultimate;

      // Map company ID field (backend may return crefo_id or company_id instead of id)
      // This ensures navigation to company details works correctly
      if (!company.id && company.crefo_id) {
        company.id = company.crefo_id;
      }
      if (!company.id && company.company_id) {
        company.id = company.company_id;
      }
    });
  }

  return person;
};

/**
 * Generates a synthetic, client-side person_crefo_id for a discovered contact
 * that the backend did not assign one to. Prefixed `disc_` so discovered
 * contacts are distinguishable from real CREFO ids and never collide with
 * navigable ids (see isNavigableCrefoId).
 */
const synthDiscoverId = () => {
  // Crypto-only (no Math.random — flagged by Sonar S2245). This id is a
  // client-only display/dedup key, not a secret, but the Web Crypto API is
  // universally available in our targets (browsers, jsdom, Node) so there is no
  // reason to fall back to a weak PRNG.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `disc_${crypto.randomUUID()}`;
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `disc_${hex}`;
};

/**
 * Normalises a single discovered contact (TD-2105, Contact Enrichment Stream B)
 * to the platform contact shape consumed by ContactCard.
 *
 * - Tags it `source: 'discovered'` (badge + dedup downstream in TD-2106).
 * - Guarantees a `disc_`-prefixed `person_crefo_id` (synthesised when absent).
 * - Maps the BE `role` scalar (TD-2272: `seniorities[0] ?? jobTitle`) onto
 *   `manager_function` so the Role row AND the role filter both work. The
 *   standalone `job_title` and `country` fields (TD-2358) plus `address`
 *   (BE-built `location[0] + ", " + country`) pass through untouched via the
 *   spread — ContactCard renders job_title as the grey line under the name and
 *   country in the Country-of-residency row (TD-2273).
 * - Defensively maps common camelCase BE fields to the snake_case the UI uses.
 */
export const mapDiscoveredContact = (raw = {}) => {
  const contact = { ...raw };

  // TD-2486: the BE discover payload uses provider-neutral keys (`company_name`,
  // `company_domain`, `discovered_person_id`). They ride through on the spread
  // above as TRANSIENT search metadata — NOT the persisted
  // `contact_enrichment.surfe_company_name/domain` columns — and nothing in Key
  // Contacts reads them today. Don't wire a generic `company_name` read off this
  // object without confirming it's the discover-transient one.

  // Defensive camelCase → snake_case normalisation. Existing snake_case fields
  // always win.
  if (contact.first_name === undefined && raw.firstName !== undefined) {
    contact.first_name = raw.firstName;
  }
  if (contact.last_name === undefined && raw.lastName !== undefined) {
    contact.last_name = raw.lastName;
  }
  // BE `role` (seniority, jobTitle fallback) → `manager_function`. Fall back to
  // camelCase variants for resilience against shape drift.
  if (
    contact.manager_function === undefined &&
    (raw.role ?? raw.jobTitle ?? raw.managerFunction) !== undefined
  ) {
    contact.manager_function = raw.role ?? raw.jobTitle ?? raw.managerFunction;
  }
  if (contact.linkedin_url === undefined && (raw.linkedinUrl ?? raw.linkedin_url) !== undefined) {
    contact.linkedin_url = raw.linkedinUrl ?? raw.linkedin_url;
  }
  // Standalone Surfe job title (TD-2358/TD-2273). The BE sends snake_case
  // `job_title`; this guards camelCase shape drift only.
  if (contact.job_title === undefined && raw.jobTitle !== undefined) {
    contact.job_title = raw.jobTitle;
  }

  contact.source = 'discovered';
  if (!contact.person_crefo_id) {
    contact.person_crefo_id = synthDiscoverId();
  }

  // TD-2422: stamp the canonical role bucket so the Key Contacts role filter
  // matches discovered contacts across the same bucket set as CREFO contacts
  // (which the BE stamps with `role_bucket`). Surfe seniority (= manager_function
  // here) is a closed enum bucketed FE-side; unknown/free-text → 'Other'.
  contact.role_bucket = bucketLabelForSurfeSeniority(contact.manager_function);
  // TD-2956: the BE sends `role_buckets` (every bucket a contact belongs to) alongside the
  // singular `role_bucket`, and the role filter matches on it. Stamp it here too so a
  // discovered contact has the same shape as a BE-served one rather than relying on every
  // consumer remembering the fallback. Always a single entry: Surfe supplies seniority, never
  // ownership, so a discovered contact can never be in the shareholder bucket.
  contact.role_buckets = [contact.role_bucket];

  return contact;
};

/**
 * Maps a `POST /enrichment/discover` response to the platform shape, tagging
 * each contact as discovered. Tolerates a missing/empty contacts array.
 */
export const mapDiscoverResponse = (data) => ({
  contacts: Array.isArray(data?.contacts) ? data.contacts.map(mapDiscoveredContact) : []
});
